// pages/admin/ProductRequest.jsx
import React, { useEffect, useState } from "react";
import TableComponent from "../../components/TableComponent";
import ApiCustom from "../../utils/ApiCustom";
import { formatDate } from "../../utils/helps";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { toast } from "react-toastify";
import { TrashIcon, EyeIcon } from "@heroicons/react/24/outline";
import StatusDropdown from "../../components/admin/StatusDropdownButton";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const ProductRequest = () => {
  const [requests, setRequests] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    if (hasPermission("PRODUCT_REQUEST_SHOW")) {
      getData();
    }
  }, []);

  const getData = async () => {
    ApiCustom("/product-request-show")
      .then((res) => {
        setRequests(res.data?.data?.data || []);
      })
      .catch((err) => console.log(err));
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await ApiCustom.put(`/product-request-update-status/${id}`, {
        status: newStatus,
      });
      toast.success("Cập nhật trạng thái thành công!");
      getData();
    } catch (error) {
      toast.error("Đã xảy ra lỗi khi cập nhật trạng thái!");
    }
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };

  const handleDelete = async () => {
    ApiCustom.delete(`/product-request-delete/${deleteId}`)
      .then(() => {
        toast.success("Xoá yêu cầu thành công");
        handleCloseDeleteModal();
        getData();
      })
      .catch(() => toast.error("Có lỗi xảy ra khi xoá yêu cầu"));
  };

  const openView = (item) => {
    setViewItem(item);
    setIsViewOpen(true);
  };
  const closeView = () => {
    setIsViewOpen(false);
    setViewItem(null);
  };

  // mapping hiển thị trạng thái (FIX #2)
  const statusLabel = (s) => {
    switch (s) {
      case "new":
        return "Mới";
      case "contacted":
        return "Đã liên hệ";
      case "done":
        return "Đã xử lý";
      case "rejected":
        return "Từ chối";
      default:
        return s || "-";
    }
  };

  const headers = [
    {
      key: "id",
      label: "STT",
      render: (item, index) => <p className="font-medium">{index + 1}</p>,
    },
    {
      key: "fullName",
      label: "Họ và tên",
      render: (item) => <p className="font-medium">{item.fullName || "-"}</p>,
    },
    {
      key: "phone",
      label: "Số điện thoại",
      render: (item) => <p className="font-medium">{item.phone || "-"}</p>,
    },
    {
      key: "productInfo",
      label: "Thông tin sản phẩm cần tìm",
      render: (item) => (
        <p
          className="font-medium break-words"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={item.productInfo || ""}
        >
          {item.productInfo || "-"}
        </p>
      ),
    },
    {
      key: "createdAt",
      label: "Thời gian",
      render: (item) => (
        <p className="font-medium">{formatDate(item?.createdAt)}</p>
      ),
    },
  ];

  if (hasPermission("PRODUCT_REQUEST_UPDATE")) {
    headers.splice(4, 0, {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <StatusDropdown
          options={[
            { value: "new", label: "Mới" },
            { value: "contacted", label: "Đã liên hệ" },
            { value: "done", label: "Đã xử lý" },
            { value: "rejected", label: "Từ chối" },
          ]}
          value={item.status}
          onChange={(stt) => handleStatusChange(item.id, stt)}
          minWidth="w-36"
          getStatusStyle={(value) => {
            switch (value) {
              case "done":
                return "bg-green-800 text-white";
              case "contacted":
                return "bg-blue-100 text-blue-800";
              case "rejected":
                return "bg-red-100 text-red-800";
              case "new":
                return "bg-green-100 text-green-800";
              default:
                return "bg-gray-100 text-gray-800";
            }
          }}
        />
      ),
    });
  }

  if (
    hasPermission("PRODUCT_REQUEST_DELETE") ||
    hasPermission("PRODUCT_REQUEST_UPDATE")
  ) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {/* Nút xem luôn luôn hiển thị (FIX #1) */}
        <button
          onClick={() => openView(item)}
          className="btn btn-outline btn-success btn-sm px-3 my-1 rounded"
          title="Xem chi tiết"
        >
          <EyeIcon className="w-5 h-5" />
        </button>

        {hasPermission("PRODUCT_REQUEST_DELETE") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xoá yêu cầu"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  const filterData = requests.filter((item) => {
    const matchText =
      searchValue === ""
        ? true
        : [item?.fullName, item?.phone, item?.productInfo]
            .filter(Boolean)
            .some((field) =>
              String(field).toLowerCase().includes(searchValue.toLowerCase()),
            );

    const matchStatus =
      !statusFilter || String(item?.status) === String(statusFilter);

    return matchText && matchStatus;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quản lý yêu cầu tìm sản phẩm</h1>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <label
              htmlFor="requestSearchFilter"
              className="text-sm font-medium w-24"
            >
              Tìm kiếm:
            </label>
            <input
              id="requestSearchFilter"
              type="search"
              className="w-full px-2 py-1 border rounded-lg focus:outline-none pr-10"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Họ tên / SĐT / nội dung..."
            />
          </div>

          <div className="flex items-center space-x-2">
            <label
              htmlFor="statusFilter"
              className="text-sm font-medium whitespace-nowrap"
            >
              Trạng thái:
            </label>
            <select
              id="statusFilter"
              className="select select-bordered select-sm w-44"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tất cả</option>
              <option value="new">Mới</option>
              <option value="contacted">Đã liên hệ</option>
              <option value="done">Đã xử lý</option>
              <option value="rejected">Từ chối</option>
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filterData}
          settings={tableSettings}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />

      {isViewOpen && viewItem && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Chi tiết yêu cầu</h3>
              <button
                onClick={closeView}
                className="px-3 py-1 rounded hover:bg-base-200"
                aria-label="Đóng"
                title="Đóng"
              >
                ✕
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Họ và tên</div>
                  <div className="font-medium">{viewItem.fullName || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Số điện thoại</div>
                  <div className="font-medium">{viewItem.phone || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Trạng thái</div>
                  <div className="font-medium">
                    {statusLabel(viewItem.status)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Thời gian</div>
                  <div className="font-medium">
                    {formatDate(viewItem.createdAt)}
                  </div>
                </div>
              </div>

              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">
                  Thông tin sản phẩm cần tìm
                </div>
                <div className="whitespace-pre-wrap break-words">
                  {viewItem.productInfo || "-"}
                </div>
              </div>
            </div>

            <div className="p-5 border-t flex justify-end">
              <button
                onClick={closeView}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductRequest;
